// Ren matematik (ingen THREE/DOM) bag 2D-profilens markør i 3D-vinduet og den valgfri synkroniserede kamerafølge.
// Holdt adskilt, så den kan testes direkte (ProfileHoverMath.test.js) uden viewer.
//
// Profilens eget rum: x = mileage (afstand langs profilens xy-forløb), z = kote. Segmenter har samme form som
// profileSegments() i ProfileOverlays.js: {start: {x, y}, dir: {x, y}, length, mileage}.

// Verdensposition på profilaksen ved et givet mileage (xy) - klemt til profilens ende, så et punkt i korridorens
// kant ved et knæk/ender aldrig giver en position uden for profilen. null hvis der ingen segmenter er.
export function axisPositionAtMileage (segments, mileage) {
	if (!segments || segments.length === 0 || !isFinite(mileage)) {
		return null;
	}

	let segment = segments[segments.length - 1];
	for (let i = 0; i < segments.length; i++) {
		if (mileage < segments[i].mileage + segments[i].length) {
			segment = segments[i];
			break;
		}
	}

	let t = Math.min(Math.max(mileage - segment.mileage, 0), segment.length);

	return {
		x: segment.start.x + segment.dir.x * t,
		y: segment.start.y + segment.dir.y * t,
		clamped: mileage < 0 || mileage > segment.mileage + segment.length
	};
}

// Samlet profillængde (summen af segmenterne)
export function profileLength (segments) {
	if (!segments || segments.length === 0) {
		return 0;
	}
	let last = segments[segments.length - 1];

	return last.mileage + last.length;
}

// Verdensskala for en sprite (uden størrelsesdæmpning), så den fylder `pixels` på skærmen uanset afstand.
// perspektiv: scale * afstand = verdenslængde; ortografisk: scale er verdenslængden direkte.
export function spriteWorldScale (pixels, canvasHeight, camera) {
	if (!(canvasHeight > 0)) {
		return 0;
	}
	if (camera.isOrthographic) {
		return pixels * (camera.top - camera.bottom) / camera.zoom / canvasHeight;
	}

	return pixels * 2 * Math.tan(camera.fov * Math.PI / 360) / canvasHeight;
}

// Billedramme-uafhængig udjævning: andel af afstanden til målet, der tages i et skridt på dt sekunder (tidskonstant tau).
export function smoothingFactor (dt, tau) {
	if (!(dt > 0)) {
		return 0;
	}
	if (!(tau > 0)) {
		return 1;
	}

	return 1 - Math.exp(-dt / tau);
}

// Akse-tal i brugerens talformat: d3's engelske format (',' tusinder, '.' decimal) omsættes til sprogets tegn.
export function localizeNumberText (text, group, decimal) {
	return String(text).replace(/[,.]/g, (c) => (c === ',' ? group : decimal));
}

// Afstand fra kameraet til fokuspunktet, så et område med radius `areaRadius` fylder billedet (lodret synsvinkel fovDeg).
export function distanceForArea (areaRadius, fovDeg) {
	let half = Math.max(fovDeg, 1) * Math.PI / 360;

	return areaRadius / Math.sin(half);
}

// Det relevante område ved første zoom: en lille del af profilens længde, men mindst nogle meter og mindst korridorens bredde.
export function followAreaRadius (length, width) {
	let fromLength = Math.min(Math.max(length * 0.2, 2), 15);
	let fromWidth = (width || 0) * 2;

	return Math.max(fromLength, fromWidth, 1.5);
}

// Kameraets retning ud fra yaw/pitch (samme konvention som View.direction i viewer/view.js).
export function viewDirection (yaw, pitch) {
	let c = Math.cos(pitch);

	return {x: -Math.sin(yaw) * c, y: Math.cos(yaw) * c, z: Math.sin(pitch)};
}

export function viewPivot (view) {
	let dir = viewDirection(view.yaw, view.pitch);

	return {
		x: view.position.x + dir.x * view.radius,
		y: view.position.y + dir.y * view.radius,
		z: view.position.z + dir.z * view.radius
	};
}

function snapshot (view) {
	return {
		x: view.position.x, y: view.position.y, z: view.position.z,
		yaw: view.yaw, pitch: view.pitch, radius: view.radius
	};
}

// Er kameraet ændret af andre end følgemodulet siden dets sidste skrivning? (manuel navigation, TWEEN-zoom, anden kamerafunktion)
export function viewChangedExternally (view, expected) {
	if (!expected) {
		return false;
	}
	let eps = 1e-6 * Math.max(1, Math.abs(expected.radius));

	return Math.abs(view.position.x - expected.x) > eps ||
		Math.abs(view.position.y - expected.y) > eps ||
		Math.abs(view.position.z - expected.z) > eps ||
		Math.abs(view.yaw - expected.yaw) > 1e-9 ||
		Math.abs(view.pitch - expected.pitch) > 1e-9 ||
		Math.abs(view.radius - expected.radius) > eps;
}

// Synkroniseret kamerafølge: kameraet glider (kun position/afstand - aldrig retning) mod den seneste gyldige profilposition.
//
// - Kun ét mål (det seneste): ingen kø af animationer, ingen nye tweens - update() flytter en lille andel pr. billede.
// - Første gyldige position efter aktivering: zoomer ind til det relevante område (kun ind, aldrig ud) og følger derefter.
// - Retningen (yaw/pitch) røres aldrig: ingen vilkårlige spring ved knæk i profilen.
// - Ændres kameraet af andre (brugerens egen navigation, anden kamerafunktion), suspenderes følgen uden at flytte kameraet tilbage.
//   Den genoptages først, når musen går ind i profilfladen igen (enter()).
export class ProfileViewSync {
	constructor (getView, getFovDeg) {
		this.getView = getView;
		this.getFovDeg = getFovDeg || (() => 60);
		this.enabled = false;
		this.tauPan = 0.12;
		this.tauZoom = 0.3;
		this.reset();
	}

	// Nulstil al tilstand (profilskift, vindue minimeret/lukket, ny aktivering): første gyldige position zoomer igen.
	reset () {
		this.target = null;
		this.radiusGoal = null;
		this.zoomed = false;
		this.suspended = false;
		this.expected = null;
		this.settled = true;
		this.context = null;
	}

	setEnabled (on) {
		on = !!on;
		if (on === this.enabled) {
			return;
		}
		this.enabled = on;
		// slået fra: kameraet bliver, hvor det er (ingen tilbagerulning); slået til: friskt udgangspunkt
		this.reset();
	}

	// Musen er (igen) kommet ind over profilens tegneflade: genoptag en suspenderet følge fra kameraets nuværende tilstand.
	enter () {
		// kameraet kan være flyttet af brugeren, mens musen var uden for profilfladen: tag det som nyt udgangspunkt
		this.suspended = false;
		this.expected = null;
	}

	// Seneste gyldige profilposition ({x, y, z} i verden) eller null (ingen gyldigt punkt: kameraet står stille).
	setTarget (position, context) {
		if (!this.enabled) {
			return;
		}
		if (!position) {
			this.target = null;
			return;
		}
		this.context = context || this.context;
		this.target = {x: position.x, y: position.y, z: position.z};

		if (!this.zoomed && !this.suspended) {
			this.zoomed = true;
			let view = this.getView();
			let ctx = this.context || {};
			let goal = distanceForArea(followAreaRadius(ctx.length || 0, ctx.width || 0), this.getFovDeg());
			// kun ind: står kameraet allerede tættere på, beholdes brugerens afstand
			this.radiusGoal = view.radius > goal ? goal : null;
		}
		this.settled = false;
	}

	// Kaldes pr. billede (viewerens "update"). Returnerer true, hvis kameraet blev flyttet.
	update (dt) {
		if (!this.enabled || this.suspended || this.settled || !this.target) {
			return false;
		}

		let view = this.getView();

		if (viewChangedExternally(view, this.expected)) {
			this.suspended = true;
			this.expected = null;
			this.radiusGoal = null;
			return false;
		}

		let pivot = viewPivot(view);
		let kPan = smoothingFactor(Math.min(dt, 0.1), this.tauPan);
		let kZoom = smoothingFactor(Math.min(dt, 0.1), this.tauZoom);

		let radius = view.radius;
		if (this.radiusGoal !== null) {
			radius += (this.radiusGoal - radius) * kZoom;
		}

		let px = pivot.x + (this.target.x - pivot.x) * kPan;
		let py = pivot.y + (this.target.y - pivot.y) * kPan;
		let pz = pivot.z + (this.target.z - pivot.z) * kPan;

		let tolerance = 1e-3 * Math.max(radius, 1);
		let close = Math.abs(this.target.x - px) < tolerance &&
			Math.abs(this.target.y - py) < tolerance &&
			Math.abs(this.target.z - pz) < tolerance;
		let zoomDone = this.radiusGoal === null || Math.abs(this.radiusGoal - radius) < tolerance;

		if (close && zoomDone) {
			px = this.target.x;
			py = this.target.y;
			pz = this.target.z;
			if (this.radiusGoal !== null) {
				radius = this.radiusGoal;
				this.radiusGoal = null;
			}
			this.settled = true;
		}

		let dir = viewDirection(view.yaw, view.pitch);
		view.position.x = px - dir.x * radius;
		view.position.y = py - dir.y * radius;
		view.position.z = pz - dir.z * radius;
		view.radius = radius;

		this.expected = snapshot(view);

		return true;
	}
}
